import { FC } from "react";
import { Shelf, Stack, useResponsive } from "capitalrx-components-2.0/lib/layout";
import { Regular, Semibold } from "capitalrx-components-2.0/lib/typography";
import { formatDate, formatDollars } from "capitalrx-utilities";

import { Finance } from "@/api/interfaces";
import { PROVIDER_MANAGEMENT_LAYOUT } from "@/constants";
import styles from "./MedicalClaimsProviderList.module.scss";

type TransactionRowProps = {
  row: Finance.MedicalClaimProviderSchema;
};

const statusLabels: Record<string, string> = {
  in_progress: "In Progress",
  approved: "Approved",
  partially_approved: "Partially Approved",
  denied: "Denied",
};

const statusColors: Record<string, string> = {
  in_progress: "#313E48",
  approved: "#317861",
  partially_approved: "#313E48",
  denied: "#B8222A",
};

const DASH = "--";
const isEmpty = (v: unknown) => v === null || v === undefined || (typeof v === "string" && v.trim() === "");
const display = (v: unknown) => (isEmpty(v) ? DASH : String(v));
const displayDate = (dateValue?: string) => {
  if (isEmpty(dateValue)) return DASH;
  const formatted = formatDate(dateValue, "MM/DD/YYYY", true);
  return isEmpty(formatted) || formatted.toLowerCase() === "invalid date" ? DASH : formatted;
};
const displayMoney = (amount?: number | string | null) => {
  if (isEmpty(amount)) return DASH;

  const n = typeof amount === "string" ? Number(amount) : amount;
  if (Number.isNaN(n as number)) return DASH;

  return formatDollars(n as number);
};

const displayWithPrefix = (prefix: string, v: unknown) => (isEmpty(v) ? DASH : `${prefix}${v}`);
const displayStatus = (status?: string) => (status && statusLabels[status] ? statusLabels[status] : DASH);

export const TransactionRow: FC<TransactionRowProps> = ({ row }) => {
  const { isCompressed } = useResponsive(PROVIDER_MANAGEMENT_LAYOUT);

  const claimStatusLabel = displayStatus(row?.claim_status);
  const claimStatusColor = row?.claim_status && statusColors[row.claim_status] ? statusColors[row.claim_status] : undefined;

  return (
    <>
      {!isCompressed && (
        <Shelf spacing="medium-large" className={styles.ClaimRow}>
          <Stack alignment="left" spacing="mini" style={{ width: "115px" }}>
            <Regular className={styles.RowText}>{displayDate(row?.start_date)}</Regular>
          </Stack>
          <Shelf.Spacer />
          <Stack alignment="left" spacing="mini" style={{ width: "179px" }}>
            <Regular className={styles.RowText}>{display(row?.external_member_id)}</Regular>
          </Stack>
          <Shelf.Spacer />
          <Stack alignment="left" spacing="mini" style={{ width: "179px" }}>
            <Regular className={styles.RowText}>{display(row?.patient_name)}</Regular>
          </Stack>
          <Shelf.Spacer />
          <Stack alignment="left" spacing="mini" style={{ width: "179px" }}>
            <Regular className={styles.RowText}>{displayWithPrefix("#", row?.patient_account)}</Regular>
          </Stack>
          <Shelf.Spacer />
          <Stack alignment="left" spacing="mini" style={{ width: "179px" }}>
            <Regular className={styles.RowText}>{displayDate(row?.submission_date)}</Regular>
          </Stack>
          <Shelf.Spacer />
          <Stack alignment="left" spacing="mini" style={{ width: "108px" }}>
            <Regular className={styles.RowText} style={claimStatusColor ? { color: claimStatusColor } : undefined}>
              {claimStatusLabel}
            </Regular>
          </Stack>
          <Shelf.Spacer />
          <Stack alignment="right" spacing="mini" style={{ width: "75px" }}>
            <Regular className={styles.RowText}>{displayMoney(row?.payment_amount)}</Regular>
          </Stack>
          <Shelf.Spacer />
          <Stack alignment="right" spacing="mini" style={{ width: "179px" }}>
            <Regular className={styles.RowText}>{displayMoney(row?.patient_pay_amount)}</Regular>
          </Stack>
          <Shelf.Spacer />
        </Shelf>
      )}
      {isCompressed && (
        <Stack spacing="small" className={styles.ClaimRow}>
          <Shelf>
            <Regular>Service Start</Regular>
            <Shelf.Spacer />
            <Semibold className={styles.RowText}>{displayDate(row?.start_date)}</Semibold>
          </Shelf>
          <Shelf>
            <Regular>Member ID</Regular>
            <Shelf.Spacer />
            <Semibold className={styles.RowText}>{display(row?.external_member_id)}</Semibold>
          </Shelf>
          <Shelf>
            <Regular>Patient Name</Regular>
            <Shelf.Spacer />
            <Semibold className={styles.RowText}>{display(row?.patient_name)}</Semibold>
          </Shelf>
          <Shelf>
            <Regular>
              Patient Account <span className={styles.Number}>#</span>
            </Regular>
            <Shelf.Spacer />
            <Semibold className={styles.RowText}>{displayWithPrefix("#", row?.patient_account)}</Semibold>
          </Shelf>
          <Shelf>
            <Regular>Submission Date</Regular>
            <Shelf.Spacer />
            <Semibold className={styles.RowText}>{displayDate(row?.submission_date)}</Semibold>
          </Shelf>
          <Shelf>
            <Regular>Claim Status</Regular>
            <Shelf.Spacer />
            <Semibold className={styles.RowText} style={claimStatusColor ? { color: claimStatusColor } : undefined}>
              {claimStatusLabel}
            </Semibold>
          </Shelf>
          <Shelf>
            <Regular>Amount Paid</Regular>
            <Shelf.Spacer />
            <Semibold className={styles.RowText}>{displayMoney(row?.payment_amount)}</Semibold>
          </Shelf>
          <Shelf>
            <Regular>Patient Responsibility</Regular>
            <Shelf.Spacer />
            <Semibold className={styles.RowText}>{displayMoney(row?.patient_pay_amount)}</Semibold>
          </Shelf>
        </Stack>
      )}
    </>
  );
};
